<template>
    <Dialog v-model:open="opened">
        <DialogTrigger> <slot /> </DialogTrigger>
        <DialogContent class="mt-16">
            <DialogHeader>
                <DialogTitle>
                    <span v-if="isEditing">
                        {{ $t("days_off.edit_days_off_modal.title") }}
                    </span>
                    <span v-else>
                        {{ $t("days_off.add_days_off_modal.title") }}
                    </span>
                </DialogTitle>
            </DialogHeader>

            <form @submit.prevent="onSubmit">
                <div class="mt-2">
                    <div class="mt-4">
                        <label
                            for="day-off-name"
                            class="text-sm font-medium text-iron-700 block"
                        >
                            {{ $t("days_off.add_days_off_modal.name") }}
                        </label>
                        <div class="mt-1 w-full">
                            <input
                                id="day-off-name"
                                v-model="name"
                                type="text"
                                data-cy="new-availability-name"
                                name="day-off-name"
                                class="rounded-md shadow-sm sm:text-sm block w-full"
                                :class="[
                                    errors.name
                                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                        : 'border-iron-300 focus:border-z-orange-500 focus:ring-z-orange-500',
                                ]"
                                :placeholder="
                                    $t(
                                        'days_off.add_days_off_modal.placeholder',
                                    )
                                "
                            />
                            <p
                                v-if="errors.name"
                                class="mt-1 text-sm text-red-600"
                            >
                                {{ errors.name }}
                            </p>
                        </div>
                    </div>

                    <div class="gap-4 flex items-end">
                        <div class="mt-4 w-full">
                            <label
                                for="day-off-start"
                                class="text-sm font-medium text-iron-700 block"
                            >
                                {{ $t("days_off.add_days_off_modal.start") }}
                            </label>
                            <div class="mt-1 w-full">
                                <DatePicker
                                    v-model="start"
                                    :locale="locale === 'cs' ? 'cs' : 'en'"
                                    :min-date="new Date()"
                                    :placeholder="$t('days_off.start_date')"
                                    @update:model-value="syncEndWithStart"
                                />
                                <p
                                    v-if="errors.start"
                                    class="mt-1 text-sm text-red-600"
                                >
                                    {{ errors.start }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="mt-4">
                        <label
                            for="day-off-end"
                            class="text-sm font-medium text-iron-700 block"
                        >
                            {{ $t("days_off.add_days_off_modal.end") }}
                        </label>
                        <div class="mt-1 w-full">
                            <DatePicker
                                v-model="end"
                                :locale="locale === 'cs' ? 'cs' : 'en'"
                                :min-date="minDate"
                                :placeholder="$t('days_off.end_date')"
                            />
                            <p
                                v-if="errors.end"
                                class="mt-1 text-sm text-red-600"
                            >
                                {{ errors.end }}
                            </p>
                        </div>
                    </div>
                </div>

                <DialogFooter class="mt-6">
                    <Button type="button" variant="outline" @click="closeModal">
                        {{ $t("cancel") }}
                    </Button>
                    <Button type="submit" :disabled="!isValid">
                        <span v-if="isEditing">
                            {{ $t("days_off.edit_days_off_modal.title") }}
                        </span>
                        <span v-else>
                            {{ $t("days_off.add_days_off_modal.title") }}
                        </span>
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import { Button } from "@/components/ui/button";
import { ref, computed, watch } from "vue";
import DatePicker from "@/components/ui/date-picker/index.vue";
import { useI18n } from "vue-i18n";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { useDayOffForm } from "@/composables/useDayOffForm";

const { locale } = useI18n();

const props = defineProps<{
    dayOffId?: number;
    dayOffName?: string | null;
    dayOffStart?: string;
    dayOffEnd?: string | null;
}>();

const opened = ref(false);

const {
    name,
    start,
    end,
    errors,
    isValid,
    isEditing,
    onSubmit,
    syncEndWithStart,
    resetForm,
} = useDayOffForm({
    dayOff: {
        id: props.dayOffId,
        name: props.dayOffName,
        start: props.dayOffStart,
        end: props.dayOffEnd,
    },
    onSuccess: () => {
        closeModal();
    },
});

const minDate = computed(() => new Date(start.value));

const closeModal = () => {
    opened.value = false;
};

// Reset form when dialog opens
watch(opened, (isOpen) => {
    if (isOpen) {
        resetForm({
            values: {
                name: props.dayOffName ?? "",
                start: props.dayOffStart ?? start.value,
                end: props.dayOffEnd ?? end.value,
            },
        });
    }
});
</script>
